import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import './CategoryActivationModal.scss';
import ModalWithConfirmationButtons from 'react/components/common/Modal/ModalWithConfirmationButtons/ModalWithConfirmationButtons';
import Dropdown from 'react/components/common/Dropdown/Dropdown/Dropdown';
import { getSummaryTextBuilder, SYNDICATION_VALUES } from 'data/audience-segment-builder-helper';

/**
 * CategoryActivationModal - Modal for configuring category segment activation
 * Displays market, advertiser, and syndication options
 */
const CategoryActivationModal = ({
  isOpen,
  segmentName,
  category,
  marketsAndAdvertisersPromise,
  dspService,
  onCancel,
  onSubmit,
  isProcessing,
}) => {
  const allAdvertisersOption = { value: -1, label: 'All advertisers', name: 'All advertisers' };

  // State
  const [selectedMarket, setSelectedMarket] = useState({});
  const [selectedAdvertiser, setSelectedAdvertiser] = useState({});
  const [syndicateTo, setSyndicateTo] = useState([]);
  const [isMarketsLoading, setIsMarketsLoading] = useState(true);
  const [isMarketsDropdownDisabled, setIsMarketsDropdownDisabled] = useState(true);
  const [isEmptyAdvertisers, setIsEmptyAdvertisers] = useState(true);
  const [marketsPromise, setMarketsPromise] = useState(Promise.resolve([]));
  const [advertisersPromise, setAdvertisersPromise] = useState(Promise.resolve([]));

  // Initialize markets and advertisers
  useEffect(() => {
    if (!marketsAndAdvertisersPromise) return;

    setMarketsPromise(marketsAndAdvertisersPromise.then((res) => res.markets));

    (async () => {
      try {
        const { markets, advertisers } = await marketsAndAdvertisersPromise;
        setSelectedMarket(markets[0] || {});
        if (advertisers.length > 0) {
          setSelectedAdvertiser(allAdvertisersOption);
        }
        setIsMarketsDropdownDisabled(markets.length <= 1);
        setIsEmptyAdvertisers(advertisers.length === 0);
        setAdvertisersPromise(
          Promise.resolve([...(advertisers.length > 0 ? [allAdvertisersOption] : []), ...advertisers])
        );
      } finally {
        setIsMarketsLoading(false);
      }
    })();
  }, [marketsAndAdvertisersPromise]);

  // Handle market change
  const handleMarketChange = useCallback(
    (newMarket) => {
      setSelectedMarket(newMarket);
      setAdvertisersPromise(
        dspService.getAmplificationAdvertiserContext(newMarket.value, 'value').then(({ advertisers }) => {
          if (advertisers.length > 0) {
            setSelectedAdvertiser(allAdvertisersOption);
          } else {
            setSelectedAdvertiser({});
          }
          setIsEmptyAdvertisers(advertisers.length === 0);
          return [...(advertisers.length > 0 ? [allAdvertisersOption] : []), ...advertisers];
        })
      );
    },
    [dspService]
  );

  // Handle advertiser change
  const handleAdvertiserChange = useCallback((newAdvertiser) => {
    setSelectedAdvertiser(newAdvertiser);
  }, []);

  // Handle submit
  const handleSubmit = useCallback(() => {
    onSubmit({
      market: selectedMarket,
      advertiser: selectedAdvertiser,
      syndicateTo,
    });
  }, [selectedMarket, selectedAdvertiser, syndicateTo, onSubmit]);

  // Summary text builders
  const marketsSummaryTextBuilder = (markets) =>
    markets.length === 0 ? 'No available markets' : markets[0]?.label || '';
  const advertisersSummaryTextBuilder = (advertisers) =>
    advertisers.length === 0 ? 'No available advertisers' : advertisers[0]?.label || '';

  const isSubmitDisabled =
    isProcessing || isMarketsLoading || !selectedMarket?.value || (!selectedAdvertiser?.value && !isEmptyAdvertisers);

  return (
    <div className="category-activation-modal-component">
      <ModalWithConfirmationButtons
        width="500px"
        modalTitle="Activate your segment"
        isOpen={isOpen || isProcessing}
        isCancelDisabled={isProcessing}
        isSubmitDisabled={isSubmitDisabled}
        submitLabel="Activate"
        onSubmit={handleSubmit}
        onCancel={onCancel}
        showSpinner={isProcessing}
        titleMargin="0 0 16px 0"
        isLightTheme={true}
        isDisabledEnterKeyPress={isSubmitDisabled}
        maxHeightBeforeScroll="550px"
      >
        <div className="category-activation-modal-content">
          {/* Segment Name */}
          <div className="segment-name-row">
            <span className="label">Segment Name:</span>
            <span className="segment-name-text">{segmentName}</span>
          </div>

          {/* Category Info */}
          {category && (
            <div className="category-info-row">
              <span className="label">Category:</span>
              <span className="category-name-text">{category.phrase || category.name || `ID: ${category.id}`}</span>
            </div>
          )}

          {/* Market Dropdown */}
          <div className="dropdown-row">
            <div className="dropdown-label">Activate to market:</div>
            <Dropdown
              promise={marketsPromise}
              selectedValue={selectedMarket}
              isMulti={false}
              isSearchable={true}
              onSelect={handleMarketChange}
              shouldCloseOnSelect={true}
              shouldCloseOnClickOutside={true}
              summaryTextBuilder={marketsSummaryTextBuilder}
              isDisabled={isMarketsDropdownDisabled}
              isLightTheme={true}
            />
          </div>

          {/* Advertiser Dropdown */}
          <div className="dropdown-row">
            <div className="dropdown-label">Advertiser:</div>
            <Dropdown
              promise={advertisersPromise}
              selectedValue={selectedAdvertiser}
              isMulti={false}
              isSearchable={true}
              onSelect={handleAdvertiserChange}
              shouldCloseOnSelect={true}
              shouldCloseOnClickOutside={true}
              summaryTextBuilder={advertisersSummaryTextBuilder}
              isDisabled={isEmptyAdvertisers}
              isLightTheme={true}
            />
          </div>

          {/* Syndication Dropdown */}
          <div className="dropdown-row">
            <div className="dropdown-label">Syndicate To:</div>
            <Dropdown
              isMulti
              values={SYNDICATION_VALUES}
              selectedValues={syndicateTo}
              summaryTextBuilder={getSummaryTextBuilder('All media providers')}
              onSelect={setSyndicateTo}
              isLightTheme={true}
            />
          </div>
        </div>
      </ModalWithConfirmationButtons>
    </div>
  );
};

CategoryActivationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  segmentName: PropTypes.string.isRequired,
  category: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    phrase: PropTypes.string,
    name: PropTypes.string,
  }),
  marketsAndAdvertisersPromise: PropTypes.object,
  dspService: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isProcessing: PropTypes.bool,
};

CategoryActivationModal.defaultProps = {
  isProcessing: false,
  category: null,
};

export default CategoryActivationModal;
